<template>
  <v-container>
    <v-row class="fill-height" align="center" justify="center">
      <!-- show department cards if any, otherwise show no-access card -->
      <template v-if="filteredRefType && filteredRefType.length">
        <template v-for="(item, i) in filteredRefType">
          <v-col :key="i" align="center">
            <v-hover v-slot="{ hover }">
              <div class="card-wrapper">
                <v-card
                  width="300px"
                  shaped
                  @click="viewItem(item.id)"
                  :elevation="hover || item.id === highlightedDeptId ? 12 : 2"
                  :class="{
                    'on-hover': hover,
                    highlighted: item.id === highlightedDeptId,
                    'gradient-bg': true,
                  }"
                >
                  <v-card-title class="text-h4 white--text">
                    <v-row
                      class="fill-height flex-column"
                      justify="space-between"
                    >
                      <p class="mt-4 subheading font-weight-bold">
                        {{ item.name }}
                      </p>

                      <div>
                        <p class="text-caption font-weight-medium font-italic">
                          {{ item.description }}
                        </p>
                      </div>

                      <div class="mt-2 d-flex align-center justify-center flex-wrap" style="gap: 4px;">
                        <v-chip small color="success" class="white--text">
                          <v-icon left small>mdi-play-circle-outline</v-icon>
                          {{ getActiveCountForDept(item.id) }} Active
                        </v-chip>
                        <!-- Archived chip commented out for now
                        <v-chip small color="grey darken-1" class="white--text">
                          <v-icon left small>mdi-archive-outline</v-icon>
                          {{ getArchivedCountForDept(item.id) }} Archived
                        </v-chip>
                        -->
                      </div>
                    </v-row>
                  </v-card-title>
                </v-card>
              </div>
            </v-hover>
          </v-col>
        </template>
      </template>

      <template v-else>
        <v-col cols="12" class="text-center">
          <v-card
            width="320px"
            shaped
            elevation="2"
            class="no-access-card mx-auto pa-4"
          >
            <v-card-title class="text-h6 d-flex align-center justify-center">
              <v-icon left color="primary" class="mr-2">mdi-lock</v-icon>
              No Department Access
            </v-card-title>
            <v-card-text>
              You currently do not have access to any departments. Please
              contact your system administrator to request access.
            </v-card-text>
          </v-card>
        </v-col>
      </template>
    </v-row>
  </v-container>
</template>

<script>
import DeptDataService from "../../services/DeptDataService";
import CasefileDataService from "../../services/CasefileDataService";
export default {
  data() {
    return {
      refType: [],
      highlightedDeptId: null,
      countsByRefType: {},
      activeCountsByRefType: {},
      archivedCountsByRefType: {},
      loadingCounts: false,
    };
  },
  mounted() {
    this.retrieveDept();
    this.retrieveCounts();

    // Check if a department ID was passed in the query
    if (this.$route.query.dept) {
      this.highlightedDeptId = parseInt(this.$route.query.dept, 10);

      // Remove the query parameter after using it
      this.$router.replace({ path: this.$route.path });
    }
  },
  computed: {
    filteredRefType() {
      const user = this.$store.state.auth.user || {};
      const roles = Array.isArray(user.roles) ? user.roles : [];
      // admins/managers see all
      if (roles.includes("ADMIN") || roles.includes("MANAGER")) {
        return this.refType;
      }
      // defensive: user.dept may be undefined
      const userDepts = Array.isArray(user.dept) ? user.dept : [];
      return this.refType.filter((x) => userDepts.includes(x.id));
    },
  },
  methods: {
    retrieveDept() {
      DeptDataService.getAll()
        .then((response) => {
          this.refType = response.data;
        })
        .catch((e) => {
          console.error(e);
        });
    },

    async retrieveCounts() {
      this.loadingCounts = true;
      try {
        const user = this.$store.state.auth.user || {};
        const params = {
          roles: user.roles,
          dept: user.dept,
          branches: user.branches,
          userId: user.id,
        };

        // Fetch total counts
        const totalResp = await CasefileDataService.getCountsBy('refType', params);
        if (totalResp.data && totalResp.data.counts) {
          this.countsByRefType = totalResp.data.counts;
        }

        // Fetch Active/Archived breakdown
        const statusResp = await CasefileDataService.getCountsByStatus('refType', params);
        if (statusResp.data && statusResp.data.counts) {
          this.activeCountsByRefType = statusResp.data.counts.active || {};
          this.archivedCountsByRefType = statusResp.data.counts.archived || {};
        }
      } catch (e) {
        console.error('Failed to fetch counts:', e);
      } finally {
        this.loadingCounts = false;
      }
    },

    getCountForDept(deptId) {
      const key = String(deptId);
      return this.countsByRefType[key] || this.countsByRefType[deptId] || 0;
    },

    getActiveCountForDept(deptId) {
      const key = String(deptId);
      return this.activeCountsByRefType[key] || this.activeCountsByRefType[deptId] || 0;
    },

    getArchivedCountForDept(deptId) {
      const key = String(deptId);
      return this.archivedCountsByRefType[key] || this.archivedCountsByRefType[deptId] || 0;
    },

    viewItem(id) {
      this.$router.push({ name: "refTabDetail", params: { id: id } });
    },
  },
};
</script>

<style scoped>
.v-card {
  transition: all 0.4s ease-in-out;
}

.gradient-bg {
  background: linear-gradient(135deg, #56ccf2 0%, #2f80ed 100%);
  position: relative;
  overflow: hidden;
}

.v-card:not(.on-hover):not(.highlighted) {
  opacity: 0.6;
}

.highlighted {
  box-shadow: 0 0 15px rgba(0, 0, 255, 0.5);
  opacity: 1;
}

.card-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* No-access card styling */
.no-access-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #e9eef5 100%);
  border: 1px dashed rgba(0, 0, 0, 0.06);
  color: #37474f;
  max-width: 360px;
}
</style>
